import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Carousel, Image, Card } from 'react-bootstrap';
import Loader from "./Loader";
import Message from "./Message";
import { useGetProductDetailsQuery } from "../slices/productsApiSlice";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight, faSearchPlus } from '@fortawesome/free-solid-svg-icons';

const THUMBNAIL_WIDTH = 110; // px per thumbnail including gap
const VISIBLE_THUMBNAILS = 5;

const ProductsItemsCarousel = ({ selectedColour }) => {
    const { id: productId } = useParams();
    const { data: product, isLoading, error } = useGetProductDetailsQuery(productId);
    const [activeIndex, setActiveIndex] = useState(0);
    const [showZoom, setShowZoom] = useState(false);
    const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
    const thumbnailRef = useRef(null);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (selectedColour && product?.colourArray && product?.imageArray) {
            const colourObj = product.colourArray.find(c => c.colour === selectedColour);
            if (colourObj && colourObj.image) {
                const imageIndex = product.imageArray.findIndex(img => img === colourObj.image);
                if (imageIndex !== -1) {
                    setActiveIndex(imageIndex);
                    if (thumbnailRef.current) {
                        thumbnailRef.current.scrollTo({
                            left: imageIndex * THUMBNAIL_WIDTH,
                            behavior: 'smooth'
                        });
                    }
                }
            }
        }
    }, [selectedColour, product]);

    const handleSelect = (selectedIndex) => {
        setActiveIndex(selectedIndex);
        // Scroll thumbnail strip to keep selected thumbnail visible
        if (thumbnailRef.current) {
            thumbnailRef.current.scrollTo({
                left: selectedIndex * THUMBNAIL_WIDTH,
                behavior: 'smooth'
            });
        }
    };

    // Scroll strip left without changing active image
    const handlePrevThumbnail = () => {
        if (product?.imageArray) {
            const newIndex = activeIndex === 0 ? product.imageArray.length - 1 : activeIndex - 1;
            setActiveIndex(newIndex);
            thumbnailRef.current.scrollBy({ left: -110, behavior: 'smooth' });
        }
    };

    const handleNextThumbnail = () => {
        if (product?.imageArray) {
            const newIndex = activeIndex === product.imageArray.length - 1 ? 0 : activeIndex + 1;
            setActiveIndex(newIndex);
            thumbnailRef.current.scrollBy({ left: 110, behavior: 'smooth' });
        }
    };

    const handleMouseDown = (e) => {
        isDragging.current = true;
        startX.current = e.pageX - thumbnailRef.current.offsetLeft;
        scrollLeft.current = thumbnailRef.current.scrollLeft;
    };

    const handleMouseLeave = () => {
        isDragging.current = false;
    };

    const handleMouseUp = () => {
        isDragging.current = false;
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        e.preventDefault();
        const x = e.pageX - thumbnailRef.current.offsetLeft;
        const walk = (x - startX.current) * 2;
        thumbnailRef.current.scrollLeft = scrollLeft.current - walk;
    };

    const handleImageMouseMove = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        setZoomPosition({ x, y });
    };

    const generateImageAlt = (index, total) => {
        if (total === 1) return `${product.name}`;
        return `${product.name} - Image ${index + 1} of ${total}`;
    };

    // Only show scroll buttons if more than 5 images
    const showScrollButtons = product?.imageArray?.length > VISIBLE_THUMBNAILS;

    return isLoading ? (
        <Loader />
    ) : error ? (
        <Message variant='danger'>{error}</Message>
    ) : (
        <Card className="my-3 p-3 rounded">
            <Card.Body>
                {/* Main image — fixed height so it never resizes */}
                <div style={{ aspectRatio: '1 / 1', width: '100%', overflow: 'hidden', position: 'relative' }}>
                    <Carousel
                        activeIndex={activeIndex}
                        onSelect={handleSelect}
                        pause='hover'
                        interval={null}
                        indicators={false}
                        fade={true}
                        className='my-carousel bg-opacity-100 mb-4'
                        style={{ height: '100%' }}
                    >
                        {product?.imageArray.map((imageUrl, index) => (
                            <Carousel.Item key={index}>
                                <div
                                    style={{ position: 'relative', overflow: 'hidden', height: '100%' }}
                                    onMouseMove={showZoom ? handleImageMouseMove : undefined}
                                    onMouseLeave={() => setShowZoom(false)}
                                    onClick={!isMobile ? () => setShowZoom(!showZoom) : undefined}
                                >
                                    {/* Zoom hint icon */}
                                    {!showZoom && !isMobile && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: '10px',
                                                right: '100px',
                                                background: 'white',
                                                border: '2px solid #3E6D9C',
                                                borderRadius: '8px',
                                                padding: '8px 12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                pointerEvents: 'none',
                                                zIndex: 5
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faSearchPlus} style={{ color: '#3E6D9C' }} />
                                            <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
                                                Zoom-in
                                            </span>
                                        </div>
                                    )}

                                    <Image
                                        src={imageUrl}
                                        alt={generateImageAlt(index, product.imageArray.length)}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            cursor: 'crosshair',
                                            display: 'block',
                                            opacity: showZoom ? 0.3 : 1,
                                            transition: 'opacity 0.2s'
                                        }}
                                    />

                                    {/* Zoom overlay */}
                                    {showZoom && (
                                        <div
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                backgroundImage: `url(${imageUrl})`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundSize: '250%',
                                                backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                                                pointerEvents: 'none',
                                                zIndex: 10
                                            }}
                                        />
                                    )}
                                </div>
                            </Carousel.Item>
                        ))}
                    </Carousel>
                </div>

                {/* Thumbnail strip */}
                <div className="d-flex justify-content-center align-items-center mt-3">
                    <button
                        className="thumbnail-scroll-button"
                        onClick={handlePrevThumbnail}
                        aria-label="View previous product image"
                    >
                        <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
                    </button>

                    {/* Thumbnail window — fixed width to show exactly 4 */}
                    <div
                        style={{
                            width: `${(100 + 10) * 4}px`,
                            overflow: 'hidden',
                        }}
                    >
                        <div
                            className="thumbnail-container"
                            ref={thumbnailRef}
                            onMouseDown={handleMouseDown}
                            onMouseLeave={handleMouseLeave}
                            onMouseUp={handleMouseUp}
                            onMouseMove={handleMouseMove}
                            role="list"
                            aria-label="Product image thumbnails"
                            style={{
                                display: 'flex',
                                overflowX: 'scroll',
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                            }}
                        >
                            {product?.imageArray.map((imageUrl, index) => (
                                <button
                                    key={index}
                                    onClick={() => handleSelect(index)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSelect(index);
                                        }
                                    }}
                                    className="thumbnail-button"
                                    style={{
                                        border: activeIndex === index ? '2px solid #3E6D9C' : '2px solid transparent',
                                        borderRadius: '4px',
                                        background: 'none',
                                        padding: 0,
                                        cursor: 'pointer',
                                        flex: '0 0 100px',
                                        marginRight: '10px',
                                        transition: 'border-color 0.2s',
                                    }}
                                    aria-label={`View image ${index + 1} of ${product.imageArray.length}`}
                                    aria-current={activeIndex === index ? 'true' : 'false'}
                                >
                                    <Image
                                        src={imageUrl}
                                        alt=""
                                        style={{
                                            width: '100px',
                                            height: '80px',
                                            objectFit: 'contain',
                                            borderRadius: '4px',
                                            display: 'block',
                                        }}
                                        draggable="false"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        className="thumbnail-scroll-button"
                        onClick={handleNextThumbnail}
                        aria-label="View next product image"
                    >
                        <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
                    </button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default ProductsItemsCarousel;